import type { EnumKind, EnumOption } from '@anytracker/core'

import { Button, IconButton } from '../../components/Button.js'
import styles from './TrackEditor.module.css'

interface Props {
  readonly kind: EnumKind
  readonly onChange: (kind: EnumKind) => void
  /** Option ids that measurements already point at, so they cannot be deleted. */
  readonly recordedOptionIds: ReadonlySet<string>
  readonly newOptionId: () => string
}

export function EnumKindEditor({
  kind,
  onChange,
  recordedOptionIds,
  newOptionId,
}: Props) {
  function replace(index: number, changes: Partial<EnumOption>) {
    onChange({
      ...kind,
      options: kind.options.map((option, i) =>
        i === index ? { ...option, ...changes } : option,
      ),
    })
  }

  function move(index: number, to: number) {
    if (to < 0 || to >= kind.options.length) {
      return
    }
    const options = [...kind.options]
    const [moved] = options.splice(index, 1)
    if (!moved) {
      return
    }
    options.splice(to, 0, moved)
    onChange({ ...kind, options: rescore(options, kind.ordered) })
  }

  function add() {
    onChange({
      ...kind,
      options: [
        ...kind.options,
        {
          id: newOptionId(),
          label: '',
          score: kind.ordered ? kind.options.length : null,
        },
      ],
    })
  }

  return (
    <>
      <div className={styles['toggle']}>
        <span className={styles['toggleText']}>
          <strong>These options have an order</strong>
          <span className={styles['toggleHint']}>
            {' '}
            An ordered list — <em>none, mild, strong</em> — can be averaged and charted as
            a line. An unordered one — <em>home, work, out</em> — can only ever be
            counted, because an average of it would be a number that looks meaningful and
            is not.
          </span>
        </span>
        <input
          type="checkbox"
          checked={kind.ordered}
          aria-label="These options have an order"
          onChange={(event) => {
            const ordered = event.target.checked
            onChange({
              ...kind,
              ordered,
              // Scores follow position by default. Someone turning this on has
              // already put the options in the order they mean.
              options: rescore(kind.options, ordered),
            })
          }}
        />
      </div>

      <div className={styles['options']}>
        {kind.options.map((option, index) => {
          const recorded = recordedOptionIds.has(option.id)
          return (
            <div
              key={option.id}
              className={`${styles['option']} ${option.archived === true ? styles['optionArchived'] : ''}`}
            >
              <input
                type="color"
                className={styles['colour']}
                value={option.colour ?? '#8b939e'}
                aria-label={`Colour for ${option.label || 'this option'}`}
                onChange={(event) => replace(index, { colour: event.target.value })}
              />

              <input
                type="text"
                className={styles['optionLabel']}
                value={option.label}
                placeholder="Label"
                aria-label={`Option ${String(index + 1)} label`}
                onChange={(event) => replace(index, { label: event.target.value })}
              />

              {kind.ordered && (
                <input
                  type="number"
                  className={styles['score']}
                  value={String(option.score ?? index)}
                  aria-label={`Score for ${option.label || 'this option'}`}
                  onChange={(event) => {
                    const parsed = Number(event.target.value)
                    replace(index, { score: Number.isFinite(parsed) ? parsed : null })
                  }}
                />
              )}

              <span className={styles['optionActions']}>
                <IconButton
                  label={`Move ${option.label || 'option'} up`}
                  disabled={index === 0}
                  onClick={() => move(index, index - 1)}
                >
                  <Arrow up />
                </IconButton>
                <IconButton
                  label={`Move ${option.label || 'option'} down`}
                  disabled={index === kind.options.length - 1}
                  onClick={() => move(index, index + 1)}
                >
                  <Arrow />
                </IconButton>

                {/*
                  Recorded options archive; unrecorded ones delete.
                  Deleting one that measurements point at would orphan every
                  entry that chose it, so the control changes rather than
                  failing on submit.
                */}
                {recorded ? (
                  <IconButton
                    label={
                      option.archived === true
                        ? `Restore ${option.label || 'option'}`
                        : `Archive ${option.label || 'option'} — it has been recorded`
                    }
                    onClick={() => replace(index, { archived: option.archived !== true })}
                  >
                    {option.archived === true ? <Restore /> : <Box />}
                  </IconButton>
                ) : (
                  <IconButton
                    label={`Remove ${option.label || 'option'}`}
                    onClick={() =>
                      onChange({
                        ...kind,
                        options: rescore(
                          kind.options.filter((_, i) => i !== index),
                          kind.ordered,
                        ),
                      })
                    }
                  >
                    <Cross />
                  </IconButton>
                )}
              </span>
            </div>
          )
        })}
      </div>

      <Button onClick={add}>Add an option</Button>
    </>
  )
}

/**
 * Renumbers scores to match position, for options that never had one set.
 *
 * Only fills gaps: an option with a deliberate score keeps it, because someone
 * who typed 0, 1, 3 meant to leave a gap. Unordered enums get `null`
 * throughout — a score on an unordered option is what would let a meaningless
 * average escape into a report.
 */
function rescore(options: readonly EnumOption[], ordered: boolean): EnumOption[] {
  return options.map((option, index) => ({
    ...option,
    score: ordered ? (option.score ?? index) : null,
  }))
}

function Arrow({ up = false }: { readonly up?: boolean }) {
  return (
    <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
      <path
        d={up ? 'M5 12l5-5 5 5' : 'M5 8l5 5 5-5'}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function Cross() {
  return (
    <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
      <path
        d="M5 5l10 10M15 5L5 15"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  )
}

function Box() {
  return (
    <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
      <path
        d="M3 6h14M4 6v9a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V6M8 10h4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function Restore() {
  return (
    <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
      <path
        d="M4 10a6 6 0 1 1 1.8 4.3M4 6v4h4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
